import { useCallback } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import useWhitelabel from "hooks/useWhitelabel";
import config from "@Config/index";
import { GA_EVENTS } from "./constants";
import { incrementClickCount } from "./clickStore";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getDataLayer = (): any[] => {
  if (typeof window !== "undefined") {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!(window as any).dataLayer) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).dataLayer = [];
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (window as any).dataLayer;
  }
  return [];
};

// eslint-disable-next-line import/prefer-default-export
export const useAnalytics = (platformOverride?: string) => {
  const { isUserLoggedIn, userData } = useSelector(
    ({ appData }: AppState) => ({
      isUserLoggedIn: appData.isUserLoggedIn,
      userData: appData.userData,
    }),
    shallowEqual,
  );

  const { partner_id } = useWhitelabel();

  const email = isUserLoggedIn
    ? userData?.data?.email || userData?.email || userData?.data?.name || "logged_in_user"
    : "anonymous";

  // Use build-time config for whitelabel check (only fires on whitelabel build)
  const isWhitelabelApp = config.IS_WHITELABEL_APP;

  // Determine platform: override > whitelabel mini > global
  const platform =
    platformOverride ||
    (isWhitelabelApp ? GA_EVENTS.PLATFORM.WHITELABEL_MINI : GA_EVENTS.PLATFORM.GLOBAL);

  const trackPropertyView = useCallback(
    (propertyId: string, propertyName?: string, customPlatform?: string) => {
      if (!isWhitelabelApp) return;
      const clickCount = incrementClickCount(email, "property", propertyId);
      const eventData = {
        action: GA_EVENTS.ACTION.PROPERTY_VIEW,
        category: GA_EVENTS.CATEGORY,
        label: GA_EVENTS.LABEL.CLICK_BEHAVIOUR,
        email,
        platform: customPlatform || platform,
        property_id: propertyId,
        property_name: propertyName,
        partner_id,
        click_count: clickCount,
      };
      // Push to dataLayer for TagHound visibility
      const dataLayer = getDataLayer();
      if (dataLayer) {
        dataLayer.push({ event: GA_EVENTS.ACTION.PROPERTY_VIEW, ...eventData });
      }
      gtagEventEmitter(eventData);
    },
    [email, platform, partner_id],
  );

  const trackTenancyView = useCallback(
    (tenancyId: string, tenancyName?: string, customPlatform?: string) => {
      if (!isWhitelabelApp) return;
      const clickCount = incrementClickCount(email, "tenancy", tenancyId);
      const eventData = {
        action: GA_EVENTS.ACTION.TENANCY_VIEW,
        category: GA_EVENTS.CATEGORY,
        label: GA_EVENTS.LABEL.CLICK_BEHAVIOUR,
        email,
        platform: customPlatform || platform,
        tenancy_id: tenancyId,
        tenancy_name: tenancyName,
        partner_id,
        click_count: clickCount,
      };
      // Push to dataLayer for TagHound visibility
      const dataLayer = getDataLayer();
      if (dataLayer) {
        dataLayer.push({ event: GA_EVENTS.ACTION.TENANCY_VIEW, ...eventData });
      }
      gtagEventEmitter(eventData);
    },
    [email, platform, partner_id],
  );

  const trackEnquireNow = useCallback(
    (
      entityId: string,
      entityType: "property" | "tenancy",
      entityName?: string,
      customPlatform?: string,
    ) => {
      if (!isWhitelabelApp) return;
      const clickCount = incrementClickCount(email, "enquire", entityId);
      const eventData = {
        action: GA_EVENTS.ACTION.ENQUIRE_NOW,
        category: GA_EVENTS.CATEGORY,
        label: GA_EVENTS.LABEL.CLICK_BEHAVIOUR,
        email,
        platform: customPlatform || platform,
        entity_id: entityId,
        entity_type: entityType,
        entity_name: entityName,
        partner_id,
        click_count: clickCount,
      };
      // Push to dataLayer for TagHound visibility
      const dataLayer = getDataLayer();
      if (dataLayer) {
        dataLayer.push({ event: GA_EVENTS.ACTION.ENQUIRE_NOW, ...eventData });
      }
      gtagEventEmitter(eventData);
    },
    [email, platform, partner_id],
  );

  return {
    trackPropertyView,
    trackTenancyView,
    trackEnquireNow,
  };
};
